import React from 'react';
import { createRoot } from 'react-dom/client';
import { SequenceConnect } from '@0xsequence/connect';
import { SequenceWalletProvider } from '@0xsequence/wallet-widget';
import App from './App.tsx';
import './index.css';
import './styles/rich-text.css';
import './i18n';
import { sequenceConnectConfig } from './config/sequenceConnect';
import { isChunkLoadError, isChunkUrl } from './utils/chunkErrorHandler';
import { FeatureFlagClient } from './feature-flag/client';
import {
  registerFeatureFlagClient,
  installDevToolsHook,
} from './feature-flag/react';
import { installServerFlagsSync } from './feature-flag/serverFlags';

// ─── Feature flags: register the global client before any render ─────────────
//
// Precedence depends on environment.
//
//   Local dev (vite dev server):
//     in-session override → env.json → false
//     CDN is NOT consulted at all. env.json on disk is the source of truth.
//     Edit env.json, save, page reloads (via the HMR hook below), new values
//     are live. No network dependency for local dev work.
//
//   Deployed (dev / prod builds):
//     in-session override → CDN → env.json (fallback) → false
//     CDN is the source of truth — flag flips propagate without a redeploy.
//     env.json is just the cold-start safety net so first paint isn't
//     all-false while waiting on the CDN response.

const ffEnv = import.meta.env.VITE_APP_ENV === 'production' ? 'prod' : 'dev';

const envJsonModules = import.meta.glob<{ default: Record<string, boolean> }>(
  './feature-flag/env.json',
  { eager: true },
);
const envFlags = Object.values(envJsonModules)[0]?.default ?? {};

// __FF_LOCAL_DEV_SERVER__ is injected by vite.config.ts via `define`. It's
// true only when running `vite dev` (the local dev server), false for any
// `vite build` output — including `pnpm run build:dev` which we preview
// via `pnpm preview`. import.meta.env.DEV is NOT a reliable check because
// `vite build --mode development` produces a bundle where DEV=true even
// though it's a built artifact, not the source-loading dev server.
declare const __FF_LOCAL_DEV_SERVER__: boolean | undefined;
const isLocalDevServer =
  typeof __FF_LOCAL_DEV_SERVER__ === 'boolean'
    ? __FF_LOCAL_DEV_SERVER__
    : false;

registerFeatureFlagClient(
  new FeatureFlagClient({
    // Skip remote fetching entirely in local dev.
    url: isLocalDevServer
      ? undefined
      : `https://ff.espotz.live/${ffEnv}/env.json`,
    defaults: envFlags,
  }),
);

// Server-resolved sensitive tier (region-gated flags like buyECCrypto):
// initial sync for an existing session + focus re-sync + logout cleanup.
installServerFlagsSync();

// Vite HMR: env.json edits don't auto-apply to the already-registered
// client singleton, so force a full reload when env.json changes in dev.
// No-op in deployed builds (import.meta.hot is undefined).
if (import.meta.hot) {
  import.meta.hot.accept('./feature-flag/env.json', () => {
    window.location.reload();
  });
}

// Install window.__ff in every environment — local, dev, qa, prod.
// Flag values are public (CDN serves them), and overrides live in the
// user's own localStorage, so there's no abuse surface beyond what they
// could already do by editing localStorage directly. Having the same
// debug API everywhere is genuinely useful for support, QA, and staged
// rollouts.
installDevToolsHook();

// Global chunk error handler - catches errors that might not reach the error boundary
let isReloading = false;

const handleChunkError = (error: unknown) => {
  // Prevent multiple reloads
  if (isReloading) return;

  if (isChunkLoadError(error)) {
    isReloading = true;
    console.warn('Chunk loading error detected, reloading page...', error);

    // Reload after a short delay to allow error to be logged
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }
};

// Handle unhandled promise rejections (common with dynamic imports)
window.addEventListener('unhandledrejection', event => {
  const error = event.reason;

  // Check if it's a chunk loading error
  if (isChunkLoadError(error)) {
    event.preventDefault(); // Prevent default error logging
    handleChunkError(error);
  }
});

// Handle window errors (catches network errors when loading scripts)
window.addEventListener(
  'error',
  event => {
    const error = event.error || new Error(event.message);
    const source = event.filename || '';

    // Check if error is related to chunk loading
    if (isChunkLoadError(error) || (source && isChunkUrl(source))) {
      event.preventDefault(); // Prevent default error logging
      handleChunkError(error);
    }
  },
  true,
); // Use capture phase to catch errors early

const root = createRoot(document.getElementById('root')!);

root.render(
  <React.StrictMode>
    <SequenceConnect config={sequenceConnectConfig}>
      <SequenceWalletProvider>
        <App />
      </SequenceWalletProvider>
    </SequenceConnect>
  </React.StrictMode>,
);
